/** Tutor UI configs: statuses, filters, tabs. */
import type { ManagedByValue } from '@/components/common/ManagedByMultiSelect';
import {
  CheckCircle2,
  MinusCircle,
  Clock,
  Archive,
  XCircle,
  FileCheck,
  Users,
  User,
  UserCheck,
  History,
  GraduationCap,
  FileText,
  Building2,
  Hourglass,
  Award,
  UserCircle,
  CalendarDays,
  RefreshCcw,
  PauseCircle,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { BadgeVariant } from '@/components/common/LabelBadge';
import { getRoleLabelPlural } from '@/utils/role-label-utils';
import {
  PERSON_PENDING_STATUS_COLOR,
  PERSON_SUSPENDED_STATUS_COLOR,
} from '@/constants/shared-catalog';
import type { TutorSituation } from '@/types/remote/tutor.types';
import type {
  TutorSituation as InPersonTutorSituation,
  TutorBackendTabView,
  TutorFilters,
} from '@/types/inperson/tutor.types';

/** Badge + icon config for a tutor status. */
export interface TutorStatusConfig {
  label: string;
  icon: LucideIcon;
  badgeVariant: BadgeVariant;
  badgeColor?: string;
}

// Engagement valorisation — aligned with mobile (yes / no / interested).
export const ENGAGEMENT_VALORISATION_OPTIONS = [
  { value: 'yes', label: 'Oui' },
  { value: 'no', label: 'Pas nécessaire' },
  { value: 'interested', label: 'Intéressé(e)' },
  { value: 'dont-know', label: 'Ne sais pas' },
] as const;

/** Display labels for engagement valorisation values. */
export const ENGAGEMENT_VALORISATION_LABEL: Record<string, string> = Object.fromEntries(
  ENGAGEMENT_VALORISATION_OPTIONS.map((o) => [o.value, o.label]),
);

/** Canonical engagement valorisation value. */
export type TutorValorisationValue = (typeof ENGAGEMENT_VALORISATION_OPTIONS)[number]['value'];

/** Default in-person tutor list filters. */
export const DEFAULT_TUTOR_FILTERS: TutorFilters = {
  search: undefined,
  statuses: [],
  subjects: [],
  establishmentId: undefined,
  departmentCodes: undefined,
  availability: [],
  engagementStartDateFrom: undefined,
  engagementStartDateTo: undefined,
  engagementEndDateFrom: undefined,
  engagementEndDateTo: undefined,
  exitDateFrom: undefined,
  exitDateTo: undefined,
  exitReason: undefined,
};

/** In-person filter group ids (`interface_elements.element_id`). */
export type FilterGroupId =
  | 'filter-search'
  | 'filter-status'
  | 'filter-role'
  | 'filter-subject'
  | 'filter-establishment'
  | 'filter-department'
  | 'filter-availability'
  | 'filter-entry-date'
  | 'filter-contract-end-date'
  | 'filter-exit-date'
  | 'filter-exit-reason';

/** All in-person filter group ids (configure-filters panel). */
export const ALL_FILTER_GROUP_IDS: FilterGroupId[] = [
  'filter-search',
  'filter-status',
  'filter-role',
  'filter-subject',
  'filter-establishment',
  'filter-department',
  'filter-availability',
  'filter-entry-date',
  'filter-contract-end-date',
  'filter-exit-date',
  'filter-exit-reason',
];

/** Human-readable label per in-person filter group. */
export const FILTER_GROUP_LABELS: Record<FilterGroupId, string> = {
  'filter-search': 'Recherche',
  'filter-status': 'Statut',
  'filter-role': 'Rôle',
  'filter-subject': 'Matières',
  'filter-establishment': 'Établissement',
  'filter-department': "Département d'affectation",
  'filter-availability': 'Disponibilité',
  'filter-entry-date': "Date d'entrée",
  'filter-contract-end-date': 'Date de fin engagement',
  'filter-exit-date': 'Date de désactivation',
  'filter-exit-reason': 'Motif de sortie',
};

/** Remote tutor management tab ids. */
export type TutorRemoteTabView = 'tutors' | 'pre_registered' | 'suspended' | 'archived';

/** Remote tutor tab labels. */
export const TUTOR_REMOTE_TAB_LABELS: Record<TutorRemoteTabView, string> = {
  get tutors() {
    return getRoleLabelPlural('tutor');
  },
  pre_registered: 'Pré-inscrits',
  suspended: 'Suspendu',
  archived: 'Archivés',
};

/** Sequential tab colors — shared between dashboard section and management page. */
export const TUTOR_REMOTE_TAB_COLORS: Record<TutorRemoteTabView, string> = {
  tutors: 'var(--color-1-nuance-1)',
  pre_registered: PERSON_PENDING_STATUS_COLOR,
  suspended: PERSON_SUSPENDED_STATUS_COLOR,
  archived: 'rgb(var(--color-error))',
};

/** Remote tutor tab icons. */
export const TUTOR_REMOTE_TAB_ICONS: Record<TutorRemoteTabView, LucideIcon> = {
  tutors: Users,
  pre_registered: Clock,
  suspended: PauseCircle,
  archived: Archive,
};

/** One forced list `userStatus` per remote tutor tab — do not send `tabView`. */
export const TUTOR_REMOTE_TAB_USER_STATUS: Record<TutorRemoteTabView, string> = {
  tutors: 'active',
  pre_registered: 'registered',
  suspended: 'suspended',
  archived: 'archived',
};

/** In-person tutor management tab ids. */
export type TutorInPersonTabView = 'tutors' | 'pre_registered' | 'to_validate' | 'to_reassign';

/** In-person tutor tab labels. */
export const TUTOR_INPERSON_TAB_LABELS: Record<TutorInPersonTabView, string> = {
  get tutors() {
    return getRoleLabelPlural('tutor');
  },
  pre_registered: 'Pré-inscrits',
  to_validate: 'À valider',
  to_reassign: 'À réaffecter',
};

/** In-person tutor tab icons. */
export const TUTOR_INPERSON_TAB_ICONS: Record<TutorInPersonTabView, LucideIcon> = {
  tutors: Users,
  pre_registered: Clock,
  to_validate: Hourglass,
  to_reassign: RefreshCcw,
};

/** In-person tutor tab colors. */
export const TUTOR_INPERSON_TAB_COLORS: Record<TutorInPersonTabView, string> = {
  tutors: 'var(--color-1-nuance-1)',
  pre_registered: PERSON_PENDING_STATUS_COLOR,
  to_validate: 'rgb(var(--status-warning))',
  to_reassign: 'rgb(var(--color-error))',
};

/** Maps each frontend tab to the backend {@link TutorBackendTabView} param. */
export const TUTOR_INPERSON_BACKEND_TAB_MAP: Record<
  TutorInPersonTabView,
  TutorBackendTabView | null
> = {
  tutors: 'tutors',
  pre_registered: 'pre_registered',
  to_validate: 'to_validate',
  to_reassign: 'to_reassign',
};

/** In-person tutor registration pipeline steps. */
export const TUTOR_INPERSON_REGISTRATION_STEPS = [
  { key: 'my_information', label: 'Informations personnelles' },
  { key: 'my_establishments', label: 'Établissements souhaités' },
  { key: 'information_session', label: "Réunion d'information" },
  { key: 'criminalRecord', label: 'Casier judiciaire', nested: 'my_documents' },
  { key: 'certification_360', label: 'Certification 360' },
  { key: 'contract', label: 'Contrat' },
] as const;

/** Criminal-record validation status badges. */
export const TUTOR_CRIMINAL_RECORD_STATUS_CONFIG: Record<
  string,
  { label: string; icon: LucideIcon; color: string }
> = {
  validated: {
    label: 'Validé',
    icon: CheckCircle2,
    color: 'rgb(var(--status-success))',
  },
  rejected: {
    label: 'Refusé',
    icon: XCircle,
    color: 'rgb(var(--color-error))',
  },
  pending: {
    label: 'En attente',
    icon: Clock,
    color: 'rgb(var(--status-pending))',
  },
};

/** Short labels for tutor assignment statuses. */
export const TUTOR_ASSIGNMENT_STATUS_LABEL: Record<string, string> = {
  validated: 'Validé',
  awaiting_coordinator_validation: 'En attente coord.',
  refused_by_recruiter: 'Refusé (rec.)',
  refused_by_coordinator: 'Refusé (coord.)',
  to_reassign: 'À réaffecter',
};

/**
 * Situations shown in the Status filter for the active tab.
 * `locked` = visible but not clickable (backend already filters via tabView).
 */
/** Situations shown in the Status filter for the active tab. */
export const TUTOR_INPERSON_STATUS_BY_TAB: Record<
  TutorInPersonTabView,
  { key: InPersonTutorSituation; locked?: boolean }[]
> = {
  pre_registered: [
    { key: 'registration_in_progress' },
    { key: 'awaiting_cj_validation' },
    { key: 'awaiting_recruiter_assignment' },
    { key: 'awaiting_certification' },
  ],
  to_validate: [{ key: 'awaiting_coordinator_validation', locked: true }],
  to_reassign: [{ key: 'to_reassign', locked: true }],
  tutors: [{ key: 'active' }, { key: 'tutoring_in_progress' }, { key: 'deactivated' }],
};

/** @deprecated Use {@link TUTOR_INPERSON_BACKEND_TAB_MAP}. */
export const TUTOR_INPERSON_TAB_STATUSES: Record<TutorInPersonTabView, string[]> = {
  tutors: ['active', 'deactivated'],
  pre_registered: ['registered', 'confirmed', 'prospect'],
  to_validate: [],
  to_reassign: [],
};

/** Situation badge config for in-person tutors. */
export const TUTOR_INPERSON_SITUATION_CONFIG: Record<InPersonTutorSituation, RemoteStatusConfig> = {
  registration_in_progress: {
    label: 'Inscription en cours',
    icon: Clock,
    badgeVariant: 'outlined',
    badgeColor: 'rgb(var(--status-pending))',
  },
  awaiting_cj_validation: {
    label: 'En attente CJ',
    icon: FileCheck,
    badgeVariant: 'outlined',
    badgeColor: 'var(--color-2-nuance-1)',
  },
  awaiting_recruiter_assignment: {
    label: "En attente d'affectation",
    icon: Users,
    badgeVariant: 'outlined',
    badgeColor: 'var(--color-5-nuance-2)',
  },
  awaiting_coordinator_validation: {
    label: 'En attente validation',
    icon: Hourglass,
    badgeVariant: 'outlined',
    badgeColor: 'var(--color-1-nuance-1)',
  },
  awaiting_certification: {
    label: 'En attente certification',
    icon: Award,
    badgeVariant: 'outlined',
    badgeColor: 'var(--color-3-nuance-1)',
  },
  active: {
    label: 'Actif',
    icon: CheckCircle2,
    badgeVariant: 'active',
    badgeColor: 'rgb(var(--status-success))',
  },
  tutoring_in_progress: {
    label: 'Tutorats en cours',
    icon: GraduationCap,
    badgeVariant: 'outlined',
    badgeColor: 'var(--color-1-nuance-2)',
  },
  to_reassign: {
    label: 'À réaffecter',
    icon: RefreshCcw,
    badgeVariant: 'outlined',
    badgeColor: 'var(--color-4-nuance-1)',
  },
  deactivated: {
    label: 'Désactivé',
    icon: MinusCircle,
    badgeVariant: 'outlined',
    badgeColor: 'rgb(var(--color-error))',
  },
};

/** Remote tutor pending-tab statuses. */
export type TutorPendingStatus = 'pending' | 'archived' | 'deleted';
/** Remote tutor active-tab statuses. */
export type TutorActiveStatus =
  | 'active_awaiting_student'
  | 'active_in_progress'
  | 'suspended'
  | 'archived'
  | 'deleted';

/** Badge + icon config for a remote tutor status. */
export interface RemoteStatusConfig {
  label: string;
  icon: LucideIcon;
  badgeVariant: BadgeVariant;
  badgeColor?: string;
}

/** Status badges for pending remote tutors. */
export const TUTOR_PENDING_STATUS_CONFIG: Record<TutorPendingStatus, RemoteStatusConfig> = {
  pending: {
    label: 'En attente',
    icon: Clock,
    badgeVariant: 'outlined',
    badgeColor: PERSON_PENDING_STATUS_COLOR,
  },
  archived: {
    label: 'Archivé',
    icon: Archive,
    badgeVariant: 'ghost',
    badgeColor: 'rgb(var(--color-error))',
  },
  deleted: { label: 'Supprimé', icon: XCircle, badgeVariant: 'disabled' },
};

/** Status badges for active remote tutors. */
export const TUTOR_ACTIVE_STATUS_CONFIG: Record<TutorActiveStatus, RemoteStatusConfig> = {
  active_awaiting_student: {
    label: 'Actif',
    icon: CheckCircle2,
    badgeVariant: 'active',
    badgeColor: 'rgb(var(--status-success))',
  },
  active_in_progress: {
    label: 'Actif',
    icon: CheckCircle2,
    badgeVariant: 'active',
    badgeColor: 'rgb(var(--status-success))',
  },
  suspended: {
    label: 'Suspendu',
    icon: PauseCircle,
    badgeVariant: 'outlined',
    badgeColor: PERSON_SUSPENDED_STATUS_COLOR,
  },
  archived: {
    label: 'Archivé',
    icon: Archive,
    badgeVariant: 'ghost',
    badgeColor: 'rgb(var(--color-error))',
  },
  deleted: { label: 'Supprimé', icon: XCircle, badgeVariant: 'disabled' },
};

/** Account-level status badges for remote tutors. */
export const TUTOR_ACCOUNT_STATUS_CONFIG: Record<string, RemoteStatusConfig> = {
  registered: {
    label: 'Inscrit',
    icon: Clock,
    badgeVariant: 'outlined',
    badgeColor: PERSON_PENDING_STATUS_COLOR,
  },
  pending: TUTOR_PENDING_STATUS_CONFIG.pending,
  active: TUTOR_ACTIVE_STATUS_CONFIG.active_awaiting_student,
  suspended: TUTOR_ACTIVE_STATUS_CONFIG.suspended,
  archived: TUTOR_ACTIVE_STATUS_CONFIG.archived,
  deleted: TUTOR_ACTIVE_STATUS_CONFIG.deleted,
};

/** Situations shown in the pre-registered tab filter. */
export const TUTOR_PRE_REGISTERED_TAB_SITUATION_FILTER_KEYS = [
  'registration_in_progress',
  'awaiting_validation',
  'awaiting_certification',
] as const satisfies readonly TutorSituation[];

/** Situations shown in the status filter for the active tab. */
export const TUTOR_ACTIVE_TAB_SITUATION_FILTER_KEYS = [
  'awaiting_student',
  'tutoring_in_progress',
] as const satisfies readonly TutorSituation[];

/** Status-filter keys for remote tutor tabs. */
export type TutorRemoteStatusFilterKey = 'pending' | 'active' | 'suspended' | 'archived';

/** Status-filter keys shown on the Tutors tab. */
export const TUTOR_ACTIVE_TAB_STATUS_FILTER_KEYS = [
  'active',
] as const satisfies readonly TutorRemoteStatusFilterKey[];

/** Status-filter chip config for remote tutor tabs. */
export const TUTOR_STATUS_FILTER_CHIP_CONFIG: Record<
  TutorRemoteStatusFilterKey,
  RemoteStatusConfig
> = {
  pending: TUTOR_PENDING_STATUS_CONFIG.pending,
  active: TUTOR_ACTIVE_STATUS_CONFIG.active_awaiting_student,
  suspended: TUTOR_ACTIVE_STATUS_CONFIG.suspended,
  archived: TUTOR_ACTIVE_STATUS_CONFIG.archived,
};

/** Color for a validated registration milestone. */
const MILESTONE_VALIDATED_COLOR = 'rgb(var(--status-success))';
/** Color for a pending registration milestone. */
const MILESTONE_PENDING_COLOR = 'rgb(var(--status-neutral))';

/** Registration milestone chip config. */
export interface MilestoneConfig {
  key: string;
  label: string;
  icon: LucideIcon;
  validatedColor: string;
  pendingColor: string;
}

/** Remote tutor registration milestones. */
export const TUTOR_REMOTE_MILESTONES: MilestoneConfig[] = [
  {
    key: 'my_information',
    label: 'Infos',
    icon: UserCircle,
    validatedColor: MILESTONE_VALIDATED_COLOR,
    pendingColor: MILESTONE_PENDING_COLOR,
  },
  {
    key: 'my_support',
    label: 'Dispo',
    icon: CalendarDays,
    validatedColor: MILESTONE_VALIDATED_COLOR,
    pendingColor: MILESTONE_PENDING_COLOR,
  },
  {
    key: 'information_session',
    label: 'RI',
    icon: Users,
    validatedColor: MILESTONE_VALIDATED_COLOR,
    pendingColor: MILESTONE_PENDING_COLOR,
  },
  {
    key: 'my_documents',
    label: 'Docs',
    icon: FileCheck,
    validatedColor: MILESTONE_VALIDATED_COLOR,
    pendingColor: MILESTONE_PENDING_COLOR,
  },
  {
    key: 'contract',
    label: 'Contrat',
    icon: FileText,
    validatedColor: MILESTONE_VALIDATED_COLOR,
    pendingColor: MILESTONE_PENDING_COLOR,
  },
];

/** Milestone completion state. */
export type MilestoneStatus = 'locked' | 'pending' | 'validated';

/** In-person tutor registration milestones. */
export const TUTOR_INPERSON_MILESTONES: MilestoneConfig[] = [
  {
    key: 'my_information',
    label: 'Infos',
    icon: UserCircle,
    validatedColor: MILESTONE_VALIDATED_COLOR,
    pendingColor: MILESTONE_PENDING_COLOR,
  },
  {
    key: 'information_session',
    label: 'RI',
    icon: Users,
    validatedColor: MILESTONE_VALIDATED_COLOR,
    pendingColor: MILESTONE_PENDING_COLOR,
  },
  {
    key: 'my_documents',
    label: 'Docs',
    icon: FileCheck,
    validatedColor: MILESTONE_VALIDATED_COLOR,
    pendingColor: MILESTONE_PENDING_COLOR,
  },
  {
    key: 'contract',
    label: 'Contrat',
    icon: FileText,
    validatedColor: MILESTONE_VALIDATED_COLOR,
    pendingColor: MILESTONE_PENDING_COLOR,
  },
];

/** Remote tutor registration step labels (canonical + legacy keys). */
export const TUTOR_REMOTE_STEP_LABELS: Record<string, string> = {
  // Canonical keys (new API)
  my_information: 'Informations',
  information_session: "Réunion d'information",
  my_support: 'Accompagnement',
  my_documents: 'Documents',
  contract: 'Contrat',
  // Legacy alias keys (kept for backward compat)
  qualification_information: 'Renseignement',
  basic_information: 'Compte',
  personal_information: 'Informations personnelles',
  official_documents: 'Documents officiels',
  program_data: 'Mon programme',
  engagement_data: 'Engagement',
  matching_data: 'Disponibilités',
  certification: 'Formation 360Learning',
};

/** Remote tutor filter group ids. */
export type TutorRemoteFilterGroupId =
  | 'filter-onboarding-status'
  | 'filter-folder-situation'
  | 'filter-registration-step'
  | 'filter-search'
  | 'filter-without-information-meeting'
  | 'filter-without-criminal-record'
  | 'filter-activatable'
  | 'filter-registration-date'
  | 'filter-program'
  | 'filter-subject'
  | 'filter-enterprise'
  | 'filter-establishment'
  | 'filter-criminal-record-validation-date'
  | 'filter-valorisation'
  | 'filter-information-meeting-participation'
  | 'filter-studies-participation'
  | 'filter-gender'
  | 'filter-capacity'
  | 'filter-level'
  | 'filter-managed-by';

/** Human-readable label per remote tutor filter group. */
export const TUTOR_REMOTE_FILTER_LABELS: Record<TutorRemoteFilterGroupId, string> = {
  'filter-onboarding-status': 'Statut',
  'filter-folder-situation': 'Situation',
  'filter-registration-step': "Étape d'inscription",
  'filter-search': 'Recherche',
  'filter-without-information-meeting': "Sans réunion d'information",
  'filter-without-criminal-record': 'Sans casier judiciaire',
  'filter-activatable': 'Activable',
  'filter-registration-date': "Date d'inscription",
  'filter-program': 'Programme',
  'filter-subject': 'Matière',
  'filter-enterprise': 'Entreprise',
  'filter-establishment': 'Établissement',
  'filter-criminal-record-validation-date': 'Date de validation du casier',
  'filter-valorisation': 'Engagement associatif valorisé',
  'filter-information-meeting-participation': "Participation à la réunion d'information",
  'filter-studies-participation': 'Participation aux études',
  'filter-gender': 'Genre',
  'filter-capacity': 'Capacité',
  'filter-level': 'Niveaux',
  'filter-managed-by': 'Géré par',
};

/** Engagement-associatif chips for the remote tutor list. Blank answers use `dont-know`. */
export const TUTOR_VALORISATION_FILTER_OPTIONS = [
  { value: 'yes', label: 'Oui', icon: CheckCircle2, color: 'rgb(var(--status-success))' },
  { value: 'no', label: 'Non', icon: XCircle, color: 'rgb(var(--color-error))' },
  {
    value: 'dont-know',
    label: 'Ne sais pas',
    icon: MinusCircle,
    color: 'rgb(var(--status-pending))',
  },
] as const;

/** One engagement-associatif filter value. */
export type TutorValorisationFilter = (typeof TUTOR_VALORISATION_FILTER_OPTIONS)[number]['value'];

/** Filter state for the remote tutors {@link DataTable}. Single source of truth. */
export interface TutorRemoteFilters {
  folderSituation?: string;
  tutorSituation?: string;
  userStatus?: string;
  managedByIds?: ManagedByValue;
  search?: string;
  sansRi?: boolean;
  sansCj?: boolean;
  activable?: boolean;
  registrationDateFrom?: string;
  registrationDateTo?: string;
  programIds?: string[];
  subject?: string;
  /** Engagement associatif valorisé. A blank answer is selected with `dont-know`. */
  valorisation?: TutorValorisationFilter;
  companyName?: string;
  establishmentName?: string;
  cjValidatedFrom?: string;
  cjValidatedTo?: string;
  participationRI?: boolean;
  participationStudies?: boolean;
  stepKeys?: string[];
  gender?: 'male' | 'female';
  minCapacity?: number;
  levelIds?: string[];
}

/** Default remote tutor list filters. */
export const DEFAULT_TUTOR_REMOTE_FILTERS: TutorRemoteFilters = {};

/** All remote tutor filter group ids (configure-filters panel). */
export const ALL_TUTOR_REMOTE_FILTER_GROUP_IDS: TutorRemoteFilterGroupId[] = [
  'filter-onboarding-status',
  'filter-folder-situation',
  'filter-registration-step',
  'filter-search',
  'filter-without-information-meeting',
  'filter-without-criminal-record',
  'filter-activatable',
  'filter-registration-date',
  'filter-program',
  'filter-subject',
  'filter-enterprise',
  'filter-establishment',
  'filter-criminal-record-validation-date',
  'filter-valorisation',
  'filter-information-meeting-participation',
  'filter-studies-participation',
  'filter-managed-by',
  'filter-gender',
  'filter-capacity',
  'filter-level',
];

/** Matching-view subset — hides Status, Situation, seniority,
 *  criminal-record validation date, partner, information-meeting participation and studies participation. */
export const ALL_TUTOR_MATCHING_FILTER_GROUP_IDS: TutorRemoteFilterGroupId[] = [
  'filter-registration-step',
  'filter-search',
  'filter-without-information-meeting',
  'filter-without-criminal-record',
  'filter-activatable',
  'filter-registration-date',
  'filter-program',
  'filter-subject',
  'filter-valorisation',
  'filter-gender',
  'filter-capacity',
  'filter-level',
  'filter-managed-by',
];

/** Remote tutor registration-step filter options. */
export const TUTOR_REGISTRATION_STEP_OPTIONS = [
  {
    value: 'my_information',
    label: 'Mes informations',
    icon: User,
    color: 'rgb(var(--color-info))',
  },
  {
    value: 'information_session',
    label: "Réunion d'information",
    icon: Users,
    color: 'var(--color-3-nuance-1)',
  },
  {
    value: 'my_support',
    label: 'Mon accompagnement',
    icon: GraduationCap,
    color: 'var(--color-1-nuance-1)',
  },
  {
    value: 'my_documents',
    label: 'Mes documents',
    icon: FileText,
    color: 'var(--color-2-nuance-1)',
  },
  {
    value: 'contract',
    label: 'Mon contrat',
    icon: FileCheck,
    color: 'var(--color-5-nuance-2)',
  },
] as const;

/** Pipeline chips on the remote pre-registered tab. */
export const TUTOR_PIPELINE_FILTER_OPTIONS = [
  {
    filterId: 'filter-without-information-meeting',
    key: 'sansRi',
    label: "Sans réunion d'information",
    icon: Users,
    color: 'rgb(var(--status-warning))',
  },
  {
    filterId: 'filter-without-criminal-record',
    key: 'sansCj',
    label: 'Sans casier judiciaire',
    icon: FileText,
    color: 'rgb(var(--color-error))',
  },
  {
    filterId: 'filter-activatable',
    key: 'activable',
    label: 'Activable',
    icon: CheckCircle2,
    color: 'rgb(var(--status-success))',
  },
] as const;

/** Standard engagement duration pills (calendar months) for remote tutors. */
export const ENGAGEMENT_DURATION_OPTIONS = [
  { value: 3, label: '3 mois' },
  { value: 6, label: '6 mois' },
  { value: 12, label: '1 an' },
] as const;

/** Minimum length for a custom engagement period (remote). */
export const TUTOR_CUSTOM_ENGAGEMENT_MIN_MONTHS = 3;

/** Tutors must already have reached this age. */
export const TUTOR_MIN_AGE = 15;

/** Certification 360 Learning upload constraints */
export const TUTOR_CERT_UPLOAD_CONFIG = {
  accept: '.pdf,.jpg,.jpeg,.png,.webp',
  maxSize: 5 * 1024 * 1024, // 5 MB
} as const;

/** 360Learning path status codes → UI labels. */
export const LEARNING360_PATH_STATUS_LABELS: Record<string, string> = {
  awaitingCorrection: 'En attente de correction',
  late: 'En retard',
  notYetStarted: 'Pas encore commencé',
  onTime: 'En cours',
  sessionNotOpened: 'Session non ouverte',
  successful: 'Réussi',
  toRetake: 'À reprendre',
  unsuccessful: 'Échoué',
};

/** Contract document upload constraints */
export const TUTOR_CONTRACT_UPLOAD_CONFIG = {
  accept: '.pdf',
  maxSize: 5 * 1024 * 1024, // 5 MB
} as const;

/** Criminal-record upload constraints. */
export const TUTOR_CRIMINAL_RECORD_UPLOAD_CONFIG = {
  accept: '.pdf,application/pdf',
  maxSize: 5 * 1024 * 1024, // 5 MB
} as const;

/** Establishment {@link Autocomplete} placeholders for reassignment. */
export const TUTOR_REASSIGNMENT_ESTABLISHMENT_PLACEHOLDER = {
  noDepartment: "Sélectionnez d'abord un département",
  loading: 'Chargement...',
  empty: 'Aucun établissement disponible',
  select: 'Sélectionnez un établissement',
} as const;

/** Assignment-refusal reasons shared by pre-refuse and refuse. */
export const TUTOR_ASSIGNMENT_REFUSAL_REASONS = [
  {
    code: 'capacity_reached',
    label: "Capacité d'accueil de l'établissement atteinte",
    requiresDescription: false,
  },
  {
    code: 'availability_mismatch',
    label: "Disponibilités du bénévole incompatibles avec les besoins de l'établissement",
    requiresDescription: false,
  },
  {
    code: 'subject_mismatch',
    label: "Matières ou niveaux proposés non adaptés aux besoins de l'établissement",
    requiresDescription: false,
  },
  {
    code: 'need_covered',
    label: 'Besoin en bénévoles déjà couvert',
    requiresDescription: false,
  },
  {
    code: 'profile_mismatch',
    label: "Profil du bénévole ne correspondant pas aux besoins actuels de l'établissement",
    requiresDescription: false,
  },
  {
    code: 'other',
    label: 'Autre',
    requiresDescription: true,
  },
];

/** Assignment refusal reasons as {@link Select} options. */
export const TUTOR_ASSIGNMENT_REFUSAL_SELECT_OPTIONS = TUTOR_ASSIGNMENT_REFUSAL_REASONS.map(
  (reason) => ({
    value: reason.code,
    label: reason.label,
  }),
);

/** Helper copy for the assignment decision panel. */
export const TUTOR_ASSIGNMENT_DECISION_DESCRIPTION =
  "Permet de traiter les choix d'établissements du tuteur jusqu'à la validation de son établissement d'affectation définitif.";

/** Impact copy shown before confirming an assignment refusal. */
export const TUTOR_ASSIGNMENT_REFUSAL_IMPACT = {
  prevalidate: 'Le bénévole sera notifié. Son dossier passera en statut « À réaffecter ».',
  validate: 'Le dossier repassera en statut « À réaffecter ».',
} as const;

/** Remote tutor detail panel tabs. */
export type TutorDetailTab =
  | 'details'
  | 'students'
  | 'registration'
  | 'info_session'
  | 'documents'
  | 'history';

/** Tabs on the remote tutor detail view. */
export const TUTOR_DETAIL_TAB_CONFIGS: Array<{
  id: TutorDetailTab;
  label: string;
  icon: LucideIcon;
}> = [
  { id: 'details', label: 'Détails', icon: User },
  { id: 'students', label: 'Mes élèves', icon: Users },
  { id: 'registration', label: 'Inscription', icon: CheckCircle2 },
  { id: 'info_session', label: "Réunion d'information", icon: UserCheck },
  { id: 'documents', label: 'Documents', icon: FileCheck },
  { id: 'history', label: 'Historique', icon: History },
];

/** In-person tutor detail panel tabs. */
export type TutorInPersonDetailTab =
  | 'details'
  | 'registration'
  | 'documents'
  | 'info_session'
  | 'assignment'
  | 'history';

export {
  ACTIVITY_SECTOR_LABEL,
  ASSIGNABILITY_CONFIG,
  BOOLEAN_PARTICIPATION_OPTIONS,
  BOOLEAN_SELECT_OPTIONS,
  CIVIL_SERVICE_BRANCH_LABEL,
  DEFAULT_SOURCE_CONFIG,
  FIELD_OF_ACTIVITY_TUTOR_LABEL,
  FOLDER_SITUATION_CONFIG,
  GENDER_APPEARANCE,
  GENDER_OPTIONS,
  GENDER_OPTIONS_WITHOUT_OTHER,
  HIGH_SCHOOL_CURRICULUM_LABEL,
  INPERSON_EXIT_REASON_OPTIONS,
  INPERSON_PRO_SITUATION_LABEL,
  PRO_SITUATION_COLOR_MAP,
  PRO_SITUATION_STATUS_LABEL,
  REGISTRATION_SOURCE_CONFIG,
  REMOTE_SUBJECT_CONFIG,
  REMOTE_SUBJECT_OPTIONS,
  STEP_COMPLETION_CONFIG,
  STUDY_LEVEL_LABEL,
  TUTOR_SITUATION_CONFIG,
  WEEKDAY_FILTER_OPTIONS,
  YES_NO_BADGE_CONFIG,
} from '@/constants/shared-catalog';

/** Tabs on the in-person tutor detail view. */
export const TUTOR_IN_PERSON_DETAIL_TAB_CONFIGS: Array<{
  id: TutorInPersonDetailTab;
  label: string;
  icon: LucideIcon;
}> = [
  { id: 'details', label: 'Détails', icon: User },
  { id: 'registration', label: 'Inscription', icon: FileText },
  { id: 'documents', label: 'Documents', icon: FileCheck },
  { id: 'info_session', label: "Réunion d'information", icon: Users },
  { id: 'assignment', label: 'Affectation', icon: Building2 },
  { id: 'history', label: 'Historique', icon: Clock },
];

/** Matches a subject UUID stored in the form. */
export const TUTOR_SUBJECT_UUID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Department codes allowed when creating or editing an in-person tutor assignment. */
export const ALLOWED_INPERSON_TUTOR_DEPARTMENT_CODES: ReadonlyArray<string> = [
  '91',
  '78',
  '92',
  '69',
  '95',
  '41',
  '75',
  '94',
] as const;
